/**
 * The **global core-action catalog** — the union of the three RAW core-action tables
 * ({@link MECH_ACTIONS} / {@link NPC_ACTIONS} / {@link PILOT_ACTIONS}), keyed so the action
 * registry can resolve a combatant's frozen `base.coreActionIds` WITHOUT ever reading its `kind`.
 *
 * **Why a composite key, and why the outward id stays bare:** the three tables *reuse bare id
 * strings for different actions* — 14 ids appear in 2+ tables and 13 have genuinely different
 * definitions (the worked example is `overwatch`: the NPC row is a wired, GM-declared affordance —
 * `status: "live"`, `route: "attack"` — while the mech/pilot rows are reference-only reactions). A
 * flat `Map<bareId, Action>` can't hold all three without silently shadowing two definitions per
 * colliding id, which would change the resolved `ActionDescriptor` for two of the three kinds. So:
 *   - the catalog is keyed by a **provenance-namespaced composite** (`"mech:skirmish"`,
 *     `"npc:overwatch"`, `"pilot:fight"`), so each of the three definitions is distinct — nothing
 *     shadows anything;
 *   - `base.coreActionIds` stores those composite keys, assembled at spawn ({@link coreActionIdsFor});
 *   - but `enumerateActions`' `coreDescriptor` emits the **bare** `action.id` on the outward
 *     `ActionDescriptor`, so every downstream consumer that keys on the bare string — the per-turn
 *     budget ledger (`canUseAction(..., "boost")`), reaction-use tracking (`bumpReactionUse(...,
 *     "overwatch")`), `apply_resolved` command batches (`kind: "hide"`), the frontend aim-mode
 *     dispatch (`aim.mode === "skirmish"`) — is untouched and descriptors are byte-identical.
 *
 * **The delimiter is `:`** — confirmed absent from every bare core-action id (the bare ids are RAW
 * names lowercased/underscored: `skirmish`, `quick_tech`, `self_destruct`, …), so a composite is
 * unambiguously `<provenance>:<bareId>` and `resolveCoreAction` never mis-splits. (The granted seam
 * `base.affordances` uses the SAME `:` delimiter for its `<itemId>:<actionIndex>` keys, so this
 * is a familiar shape, not a new convention.)
 *
 * **Pure data** — built once at module load, no I/O.
 */

import { MECH_ACTIONS, type MechAction } from "./mechActions";
import { NPC_ACTIONS, type NpcAction } from "./npcActions";
import { PILOT_ACTIONS, type PilotAction } from "./pilotActions";

/** A resolved core action — one of the three RAW table shapes (they share `id`/`name`/`cost`/`category`/`blurb`/`status`/`route`/`value?`). */
export type CoreAction = MechAction | NpcAction | PilotAction;

/**
 * A core-action table's **provenance** — the composite-id prefix. This is a spawn-recipe *input*
 * (which RAW table a spawn adapter assembles a kind's core set from), NOT a runtime classifier: it's
 * baked into a frozen data key at spawn, and the registry never reads a combatant's `kind` to pick
 * it back apart — it resolves the composite against the catalog. That is what keeps the composite
 * prefix clean: provenance in a frozen key, not a `kind` dispatch.
 */
export type CoreActionProvenance = "mech" | "npc" | "pilot";

/** The composite-key delimiter — see the module doc: `:` is absent from every bare core-action id. */
const SEP = ":";

/** The three RAW core-action tables, indexed by provenance — the union the catalog is built from. */
const CORE_ACTION_TABLES: Record<CoreActionProvenance, readonly CoreAction[]> = {
  mech: MECH_ACTIONS,
  npc: NPC_ACTIONS,
  pilot: PILOT_ACTIONS,
};

/**
 * The composite core-action id list for one provenance table — `["mech:skirmish", "mech:boost", …]`
 * in the table's own order. **The spawn adapters call this** to assemble a kind's frozen
 * `base.coreActionIds`: a mech/drone/object/deployable stamps `coreActionIdsFor("mech")`, an NPC
 * `coreActionIdsFor("npc")`, a pilot `coreActionIdsFor("pilot")`.
 */
export function coreActionIdsFor(provenance: CoreActionProvenance): string[] {
  return CORE_ACTION_TABLES[provenance].map((a) => `${provenance}${SEP}${a.id}`);
}

/**
 * The global catalog: composite `"<provenance>:<bareId>"` → its {@link CoreAction} definition. Built
 * once at module load from the union of the three tables; each colliding bare id (e.g. `overwatch`)
 * keeps three distinct entries (`mech:overwatch`, `npc:overwatch`, `pilot:overwatch`) — no shadowing.
 */
export const CORE_ACTION_CATALOG: ReadonlyMap<string, CoreAction> = (() => {
  const m = new Map<string, CoreAction>();
  for (const provenance of Object.keys(CORE_ACTION_TABLES) as CoreActionProvenance[]) {
    for (const a of CORE_ACTION_TABLES[provenance]) m.set(`${provenance}${SEP}${a.id}`, a);
  }
  return m;
})();

/**
 * Resolve a composite core-action id (`"npc:overwatch"`) to its {@link CoreAction} definition, or
 * `undefined` if the id isn't in the catalog (an off-shape/unknown composite — the registry
 * honest-degrades it to no descriptor, mirroring the granted seam's missing-id handling). The
 * caller reads the resolved action's own **bare** `id` for the outward descriptor.
 */
export function resolveCoreAction(compositeId: string): CoreAction | undefined {
  return CORE_ACTION_CATALOG.get(compositeId);
}
